import { useTranslates } from 'providers/translates';
import styles from '../../styles.module.scss';
import { TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { TextFieldWithCustomPlaceholder } from 'components/CreateMemorialQuiltComponents/TextFieldWithCustomPlaceholder/TextFieldWithCustomPlaceholder';
import { DateInput, TFields, TFieldsDate } from '../../model';
import { isVac } from 'utilities/is-vac';

type Props = {
  firstName?: string;
  lastName?: string;
  dateOfBirthInput: DateInput;
  dateOfPassingInput: DateInput;
  setValueToEditableQuilt: (key: TFields, value: string) => void; 
  handleSetDay: (nameDate: TFieldsDate, date: Date | null) => void;
  handleSetMonth: (nameDate: TFieldsDate, date: Date | string | null) => void;
  handleSetYear: (nameDate: TFieldsDate, date: Date | null) => void;
};

export const Remembering: React.FC<Props> = ({
  firstName,
  lastName,
  dateOfBirthInput,
  dateOfPassingInput,
  setValueToEditableQuilt,
  handleSetDay,
  handleSetMonth,
  handleSetYear
}) => {
  const { translates } = useTranslates();
  const tellUsAboutVeteran = translates?.quiltBuilderStepOne?.tellUsAboutVeteran;

  return (
    <div className={styles.remembering}>
      <div className={styles.name_block}>
        <div className="inputWithTitle">
          <span>
            {tellUsAboutVeteran?.firstNameOfVeteranFieldLabel || 'First name'}
            <span className="requiredSymbol">*</span>
          </span>
          <TextField
            className="customInput"
            value={firstName || ''}
            onChange={({ target }) => { 
              setValueToEditableQuilt('firstName', target.value);
            }}
            variant="outlined"
            placeholder={tellUsAboutVeteran?.firstNameOfVeteranFieldEmpty || 'First name'}
            required
          />
        </div>

        <div className="inputWithTitle">
          <span>
            {tellUsAboutVeteran?.lastNameOfVeteranFieldLabel ?? 'LAST NAME'}<span className="requiredSymbol">*</span>
          </span>
          <TextField
            className="customInput"
            value={lastName || ''}
            onChange={({ target }) => {
              setValueToEditableQuilt('lastName', target.value);
            }}
            variant="outlined"
            placeholder={tellUsAboutVeteran?.lastNameOfVeteranFieldEmpty || 'Last name'}
            required
          />
        </div>
      </div>

      {!isVac() && (
        <div className={styles.date_block}>
          <div className={styles.date_box}>
            <p className={styles.date_box__title}>Date of birth</p>
            <div className={styles.date_wrapper}>
              <div className="inputWithTitle">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd"
                    views={['day']}
                    renderInput={(params) => (
                      <TextField className="datePickerInput customSvgIconDatePicker" {...params} />
                    )}
                    value={dateOfBirthInput.day}
                    onChange={(date) => handleSetDay('dateOfBirth', date)}
                  />
                </LocalizationProvider>
              </div>
              <div className="inputWithTitle">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="MM"
                    disableMaskedInput
                    views={['month']}
                    renderInput={(params) => (
                      <TextField className="datePickerInput customSvgIconDatePicker" {...params} />
                    )}
                    value={dateOfBirthInput.month}
                    onChange={(date) => handleSetMonth('dateOfBirth', date)}
                  />
                </LocalizationProvider> 
              </div>
              <div className="inputWithTitle">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="yyyy"
                    views={['year']}
                    value={dateOfBirthInput.year}
                    onChange={(date) => handleSetYear('dateOfBirth', date)}
                    renderInput={(params) => (
                      <TextFieldWithCustomPlaceholder
                        params={params}
                        value={dateOfBirthInput.year}
                      />
                    )}
                    maxDate={dateOfPassingInput.year || new Date()}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>

          <div className={styles.date_box}>
            <p className={styles.date_box__title}>Date of death</p>
            <div className={styles.date_wrapper}>
              <div className="inputWithTitle">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd"
                    views={['day']}
                    renderInput={(params) => (
                      <TextField className="datePickerInput customSvgIconDatePicker" {...params} />
                    )}
                    value={dateOfPassingInput.day}
                    onChange={(date) => handleSetDay('dateOfPassing', date)}
                  />
                </LocalizationProvider>
              </div>
              <div className="inputWithTitle">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="MM"
                    disableMaskedInput
                    views={['month']}
                    renderInput={(params) => (
                      <TextField className="datePickerInput customSvgIconDatePicker" {...params} />
                    )}
                    value={dateOfPassingInput.month}
                    onChange={(date) => handleSetMonth('dateOfPassing', date)}
                  />
                </LocalizationProvider>
              </div>
              <div className="inputWithTitle">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="yyyy"
                    views={['year']}
                    value={dateOfPassingInput.year}
                    onChange={(date) => handleSetYear('dateOfPassing', date)}
                    renderInput={(params) => (
                      <TextFieldWithCustomPlaceholder
                        params={params}
                        value={dateOfPassingInput.year}
                      />
                    )}
                    minDate={dateOfBirthInput.year || undefined}
                    maxDate={new Date()}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
